import mergeObjects from 'lodash/merge';
import { DateTime, Duration } from 'luxon';
import type { PartialDeep } from 'type-fest';
import * as Yup from 'yup';

import {
  UpdateApprovedQuoteRequest,
  UpdateApprovedQuoteRequestWireFormat,
} from '@core/types/api/UpdateApprovedQuoteRequest/UpdateApprovedQuoteRequest';
import { EmptyTenantDefaultMargins, TenantDefaultMargins } from '@core/types/tenantDefaultMargins';
import { escalationPercentToChangeRatio } from '@core/utils/decimals';
import { hasStringValue, hasValue } from '@core/utils/equality';
import { ParseNullableCurrency } from '@core/utils/formParsingHelpers';

import type { ContactData } from '../hooks/useContactCreation';
import { NewCompany } from '../types/api/Company/Company';
import { Cost } from '../types/api/Cost/Cost';
import { Currency } from '../types/api/Currency/Currency';
import Enums from '../types/api/Enums';
import type { ProjectProduct } from '../types/api/ProjectProduct/ProjectProduct';
import { NewQuote, Quote, QuoteWireFormat } from '../types/api/Quote/Quote';
import type { QuoteCompetitorWireFormat } from '../types/api/QuoteCompetitor/QuoteCompetitor';
import type { QuoteConfigProduct } from '../types/api/QuoteConfigProduct/QuoteConfigProduct';
import { QuotePlant } from '../types/api/QuotePlant/QuotePlant';
import type { QuotePolicyViolationWireFormat } from '../types/api/QuotePolicyViolation/QuotePolicyViolation';
import {
  NewQuoteProduct,
  NewQuoteProductFromWireFormat,
  type QuoteProduct,
} from '../types/api/QuoteProduct/QuoteProduct';
import type { QuoteStatus } from '../types/api/QuoteStatus/QuoteStatus';
import type { QuoteSummary } from '../types/api/QuoteSummary/QuoteSummary';
import type { PartialExcept } from '../types/app';
import { ParseServerDate } from '../utils/dateHelpers';
import {
  FormikNullableIntegrationSummary,
  QuotePriceEscalationToFormik,
} from '../utils/formikHelpers';
import { logger } from '../utils/logger';
import { RemoveNullProperties } from '../utils/types';
import { NIL_UUID, isValidUUID, randomUUID } from '../utils/uuid';
import { ProductDeliveryTypeIsValid } from '../utils/withDeliveryPriceCalculations';
import {
  TestForUndefined,
  YupContacts,
  YupEnum,
  YupNullableEnum,
  YupNullableLocalDate,
  YupNullableNumber,
  YupNullableReference,
  YupNullableString,
  YupNumber,
  YupQuotePriceEscalations,
  YupReference,
  type YupSchemaNullableReferenceType,
  type YupSchemaNumberType,
  type YupSchemaQuotePriceEscalationType,
  type YupSchemaReferenceType,
  YupSometimesNullableLocalDate,
  YupSometimesNullableReference,
  YupSometimesNullableString,
  YupString,
} from '../utils/yupHelpers';
import { NewDeliveryCostOrNullFromFormik } from './plant';
import {
  competitorLossFormikSchemaFields,
  competitorLossFromFormik,
  competitorLossToFormik,
  type CompetitorLossFormikType,
} from './projectOrQuoteCompetitor';
import {
  AdditionalProjectOrQuoteProductsFormikSchema,
  MainProjectOrQuoteProductsFormikSchema,
  type ProjectOrQuoteProductEditableFieldsFormikType,
  type ProjectOrQuoteProductFormikType,
  QuoteProductToProjectOrQuoteProductFormik,
} from './projectOrQuoteProduct';

export type ApprovedQuoteFormikType = Omit<
  UpdateApprovedQuoteRequestWireFormat,
  'status' | 'competitor'
> &
  CompetitorLossFormikType & {
    status: QuoteStatusFormikType;
  };

export const NewApprovedQuoteSchemaFormik = (
  requiresLossDetails: boolean,
): Yup.SchemaOf<ApprovedQuoteFormikType> =>
  Yup.object().shape({
    version: YupNullableNumber({ label: 'Version', integer: true }),
    status: YupReference('Status', {
      systemBehavior: YupNullableEnum(Enums.QuoteStatusSystemBehavior, 'System behavior'),
    }),
    projectJobNumber: YupNullableString('Job number'),
    purchaseOrderNumber: YupNullableString('Purchase order'),
    ...competitorLossFormikSchemaFields,
    lossReason: YupNullableReference('Loss Reason').test(
      'loss-reason-required-when-lost',
      'Loss reason is required when quote is Lost',
      function testLossReasonRequired(value) {
        if (!requiresLossDetails) {
          return true;
        }
        const status = (this.parent as { status?: { systemBehavior?: string | null } }).status;
        if (status?.systemBehavior === Enums.QuoteStatusSystemBehavior.Lost) {
          return hasValue(value?.id);
        }
        return true;
      },
    ),
    competitor: YupNullableReference('Competitor').test(
      'competitor-required-when-lost',
      'Competitor is required when quote is Lost',
      function testCompetitorRequired(value) {
        if (!requiresLossDetails) {
          return true;
        }
        const status = (this.parent as { status?: { systemBehavior?: string | null } }).status;
        if (status?.systemBehavior === Enums.QuoteStatusSystemBehavior.Lost) {
          return hasValue(value?.id);
        }
        return true;
      },
    ),
  });

export const FormikApprovedQuote = (quote: Quote | undefined): ApprovedQuoteFormikType => ({
  version: quote?.version ?? null,
  status: FormikQuoteStatus(quote?.status),
  projectJobNumber: quote?.projectJobNumber ?? null,
  purchaseOrderNumber: quote?.purchaseOrderNumber ?? null,
  ...competitorLossToFormik(quote?.competitor),
});

/** Takes the values the server assigned on save, so a later save is made against the stored version. */
export const FormikQuoteAfterUpsert = <T extends QuoteFormikType>(values: T, saved: Quote): T => ({
  ...values,
  id: saved.id,
  quoteNumber: saved.quoteNumber,
  revisionNumber: saved.revisionNumber,
  version: saved.version,
  status: FormikQuoteStatus(saved.status),
});

export const formikApprovedQuoteToWire = (
  values: ApprovedQuoteFormikType,
): UpdateApprovedQuoteRequest =>
  new UpdateApprovedQuoteRequest({
    version: values.version,
    status: values.status,
    purchaseOrderNumber: values.purchaseOrderNumber,
    projectJobNumber: values.projectJobNumber,
    competitor: competitorLossFromFormik(values),
  });

export type QuoteStatusFormikType = YupSchemaReferenceType & {
  systemBehavior: Enums.QuoteStatusSystemBehavior | null;
};
type QuoteStatusSchemaFormikType = Yup.SchemaOf<QuoteStatusFormikType>;

export const QuoteStatusSchemaFormik: QuoteStatusSchemaFormikType = YupReference('quote status', {
  systemBehavior: YupNullableEnum(Enums.QuoteStatusSystemBehavior, 'System behavior'),
});

export type RequiredQuoteFieldsInfo = {
  terms: boolean;
  configurationTemplate: boolean;
  company: boolean;
  contact: boolean;
  expirationDate: boolean;
  project: boolean;
  productDeliveryType: boolean;
  projectJobNumber: boolean;
  lossDetails: boolean;
  priceEscalation: boolean;
};
export type ProductsByPlantFormikType = {
  /** Client-only row identity, stable across plant changes/reorders/add-remove; never sent to
   * the server. Keys UI state (e.g. the buyer-pricing reconciliation latch). */
  clientId: string;
  plant: YupSchemaReferenceType;
  plantDistance: string | null;
  plantDistanceUnit: Enums.Unit | null;
  plantDriveTime: string | null;
  products: Omit<ProjectOrQuoteProductFormikType, 'displayOnly'>[];
};

export const FormikQuoteStatus = (
  quoteStatusData: QuoteStatus | undefined,
): QuoteStatusFormikType => {
  if (quoteStatusData === undefined || quoteStatusData.id === NIL_UUID) {
    return { id: null, option: null, systemBehavior: null };
  }
  return {
    id: quoteStatusData.id,
    option: {
      value: quoteStatusData.id,
      label: quoteStatusData.name,
    },
    systemBehavior: quoteStatusData.systemBehavior ?? null,
  };
};

export type QuoteFormikType = Omit<
  QuoteWireFormat,
  | 'project'
  | 'company'
  | 'contacts'
  | 'products'
  | 'priceEscalations'
  | 'user'
  | 'quoteConfig'
  | 'revenue'
  | 'estimatedVolume'
  | 'readyMixQuantity'
  | 'readyMixQuantityUnit'
  | 'aggregateQuantity'
  | 'aggregateQuantityUnit'
  | 'asphaltQuantity'
  | 'asphaltQuantityUnit'
  | 'revisionNumber'
  | 'status'
  | 'policyViolations'
  | 'divisionId'
  | 'plants'
  | 'lastStatusChangeAt'
  | 'lastStatusChangeBy'
  | 'revisionOrigin'
  | 'competitor'
  | 'exportAttempts'
  | 'exportBlockers'
> &
  CompetitorLossFormikType & {
    project: YupSchemaReferenceType;
    revisionNumber: YupSchemaNumberType;
    company: YupSchemaNullableReferenceType;
    contacts: ContactData[];
    mainProductsByPlants: ProductsByPlantFormikType[];
    additionalProductsByPlants: ProductsByPlantFormikType[];
    priceEscalations: YupSchemaQuotePriceEscalationType[];
    user: YupSchemaReferenceType;
    quoteConfig: YupSchemaNullableReferenceType;
    status: QuoteStatusFormikType;
  };

export type QuoteBulkEditFormikType = Pick<QuoteWireFormat, 'expirationDate'> & {
  user: YupSchemaNullableReferenceType;
  statusId: string | null;
};

export type QuoteStatusBulkEditRequestPayload = Pick<QuoteStatus, 'id' | 'priority'>;

export type QuotesBulkEditRequestPayload = {
  id: string;
  version: number;
  status?: PartialExcept<Quote['status'], 'id'>;
  expirationDate?: Quote['expirationDate'];
  user?: PartialExcept<Quote['user'], 'id'>;
};

/** One item per quote, each made against the version its row was loaded at, carrying the changed fields. */
export const QuotesBulkEditRequest = (
  quotes: readonly QuoteSummary[],
  changes: Partial<QuoteBulkEditFormikType>,
): QuotesBulkEditRequestPayload[] =>
  quotes.map((quote) => {
    const { statusId, expirationDate, user } = changes;
    const item: QuotesBulkEditRequestPayload = { id: quote.id, version: quote.version };

    if (hasValue(statusId)) {
      item.status = { id: statusId };
    }
    if (hasValue(expirationDate)) {
      item.expirationDate = ParseServerDate(expirationDate);
    }
    if (hasValue(user?.id)) {
      item.user = { id: user.id };
    }

    return item;
  });

export const QuoteBulkEditSchemaFormik: Yup.SchemaOf<QuoteBulkEditFormikType> = Yup.object().shape({
  expirationDate: YupNullableLocalDate('Expiration date'),
  user: YupNullableReference('User'),
  statusId: YupNullableString('Status'),
});

const sharedQuoteSchemaFields = {
  status: YupReference('Status', {
    systemBehavior: YupNullableEnum(Enums.QuoteStatusSystemBehavior, 'System behavior'),
  }),
  quoteNumber: Yup.string().default('').label('Quote number'),
  name: YupString('Name'),
  integrationSummary: Yup.mixed().nullable(),
  revisionNumber: YupNumber({
    label: 'Revision number',
    integer: true,
    positive: true,
  }),
  version: YupNullableNumber({ label: 'Version', integer: true }),
  terms: YupNullableString('Terms/T&C'),
  notes: YupNullableString('Notes (Internal Only)'),
  purchaseOrderNumber: YupNullableString('Purchase order'),
  projectJobNumber: YupNullableString('Job number'),
  expirationDate: YupNullableLocalDate('Expiration date'),
  project: YupReference('Project'),
  company: YupNullableReference('Company'),
  contact: YupNullableReference('Contact'),
  restrictOrdersToProjectProducts: Yup.boolean()
    .default(false)
    .label('Restrict to project products'),
  restrictOrdersToProjectMixProducts: Yup.boolean()
    .default(false)
    .label('Restrict to project mixes'),
  contacts: YupContacts(),
  user: YupReference('User'),
  creationDate: YupNullableLocalDate('Creation date'),
  revisionDate: YupNullableLocalDate('Revision date'),
  firstSentAt: YupNullableLocalDate('First sent at'),
  lastSentAt: YupNullableLocalDate('Last sent at'),
  quoteConfig: YupNullableReference('Quote config'),
  priceEscalations: YupQuotePriceEscalations('Price escalations'),
};

type QuoteSchemaFormikType = Yup.SchemaOf<
  Omit<
    QuoteFormikType,
    | 'id'
    | 'externalID'
    | 'externalIDs'
    | 'submitType'
    | 'status'
    | 'mainProductsByPlants'
    | 'additionalProductsByPlants'
  > & {
    mainProductsByPlants: Omit<ProductsByPlantFormikType, 'products'>[] &
      {
        products: ProjectOrQuoteProductEditableFieldsFormikType[];
      }[];
    additionalProductsByPlants: Omit<ProductsByPlantFormikType, 'products'>[] &
      {
        products: ProjectOrQuoteProductEditableFieldsFormikType[];
      }[];
  }
>;

export const LOCATION_NEEDS_PRODUCT_TITLE = 'This location needs a product';
export const LOCATION_NEEDS_PRODUCT_MESSAGE =
  'Add at least one product from this location, or remove the location.';
export const LOCATION_HAS_NO_PRODUCTS_TITLE = 'This location has no products';
export const LOCATION_HAS_NO_PRODUCTS_MESSAGE = 'Pick another location, or remove this one.';

/** This schema will be used for yup validation */
export const QuoteSchemaFormik: QuoteSchemaFormikType = Yup.object()
  .shape({
    ...sharedQuoteSchemaFields,
    ...competitorLossFormikSchemaFields,
    mainProductsByPlants: Yup.array().of(
      Yup.object({
        plant: YupReference('Location'),
        plantDistance: YupNullableString('Location distance'),
        plantDistanceUnit: YupNullableEnum(Enums.Unit, 'Location distance unit'),
        plantDriveTime: YupNullableString('Drive time'),
        products: MainProjectOrQuoteProductsFormikSchema,
      }).test('has-products-if-plant-selected', LOCATION_NEEDS_PRODUCT_MESSAGE, (value) => {
        if (value?.plant?.id) {
          return value.products ? value.products.length > 0 : false;
        }
        return true;
      }),
    ),
    additionalProductsByPlants: Yup.array().of(
      Yup.object({
        plant: YupReference('Location'),
        plantDistance: YupNullableString('Location distance'),
        plantDistanceUnit: YupNullableEnum(Enums.Unit, 'Location distance unit'),
        plantDriveTime: YupNullableString('Drive time'),
        products: AdditionalProjectOrQuoteProductsFormikSchema,
      }).test('has-products-if-plant-selected', LOCATION_NEEDS_PRODUCT_MESSAGE, (value) => {
        if (value?.plant?.id) {
          return value.products ? value.products.length > 0 : false;
        }
        return true;
      }),
    ),
  })
  .test(TestForUndefined('QuoteSchemaFormik'));

export const NewQuoteSchemaFormik: (
  requiredFields: RequiredQuoteFieldsInfo,
  requiresContactInfo: boolean,
  isMultiLocationEnabled?: boolean,
  requiresContactEmail?: boolean,
) => QuoteSchemaFormikType = (
  requiredFields: RequiredQuoteFieldsInfo,
  requiresContactInfo: boolean,
  isMultiLocationEnabled = true,
  requiresContactEmail = false,
): QuoteSchemaFormikType => {
  const deliveryTypeTestConfig = {
    name: 'required-product-delivery-type',
    message: 'Delivery type is required for all aggregate and asphalt products',
    test: ProductDeliveryTypeIsValid,
  } as const;

  let schema: QuoteSchemaFormikType = QuoteSchemaFormik.shape({
    company: YupSometimesNullableReference('Company', requiredFields.company),
    contacts: YupContacts(requiredFields.contact, requiresContactInfo, requiresContactEmail),
    terms: YupSometimesNullableString('Terms/T&C', requiredFields.terms),
    projectJobNumber: YupSometimesNullableString(
      'Project Job Number',
      requiredFields.projectJobNumber,
    ),
    quoteConfig: YupSometimesNullableReference(
      'Configuration template',
      requiredFields.configurationTemplate,
    ),
    expirationDate: YupSometimesNullableLocalDate('Expiration Date', requiredFields.expirationDate),
    project: YupSometimesNullableReference('Project', requiredFields.project),
    lossReason: YupNullableReference('Loss Reason').test(
      'loss-reason-required-when-lost',
      'Loss reason is required when quote is Lost',
      function testLossReasonRequired(value) {
        if (!requiredFields.lossDetails) {
          return true;
        }
        const status = (this.parent as { status?: { systemBehavior?: string | null } }).status;
        if (status?.systemBehavior === Enums.QuoteStatusSystemBehavior.Lost) {
          return hasValue(value?.id);
        }
        return true;
      },
    ),
    competitor: YupNullableReference('Competitor').test(
      'competitor-required-when-lost',
      'Competitor is required when quote is Lost',
      function testCompetitorRequired(value) {
        if (!requiredFields.lossDetails) {
          return true;
        }
        const status = (this.parent as { status?: { systemBehavior?: string | null } }).status;
        if (status?.systemBehavior === Enums.QuoteStatusSystemBehavior.Lost) {
          return hasValue(value?.id);
        }
        return true;
      },
    ),
    ...(requiredFields.priceEscalation && {
      priceEscalations: YupQuotePriceEscalations('Price escalations').min(
        1,
        'At least one price escalation is required',
      ),
    }),
    ...(requiredFields.productDeliveryType && {
      mainProductsByPlants:
        QuoteSchemaFormik.fields['mainProductsByPlants'].test(deliveryTypeTestConfig),
      additionalProductsByPlants:
        QuoteSchemaFormik.fields['additionalProductsByPlants'].test(deliveryTypeTestConfig),
    }),
  }) as QuoteSchemaFormikType;

  // When multi-location is disabled, ensure there's only one unique location across all plants
  if (!isMultiLocationEnabled) {
    schema = schema.test(
      'single-location-only',
      'Only one location is allowed when multi-location is disabled',
      (values) => {
        if (values === undefined) return true;

        const allPlants = [
          ...(values.mainProductsByPlants ?? []),
          ...(values.additionalProductsByPlants ?? []),
        ] as Array<{ plant?: { id?: string | null } }>;

        const uniquePlantIds = new Set(
          allPlants.map((p) => p.plant?.id).filter((id) => id != null),
        );

        return uniquePlantIds.size <= 1;
      },
    ) as QuoteSchemaFormikType;
  }

  return schema;
};

type QuoteSchemaWireType = Yup.SchemaOf<
  Omit<
    QuoteFormikType,
    | 'id'
    | 'externalID'
    | 'externalIDs'
    | 'submitType'
    | 'template'
    | 'mainProductsByPlants'
    | 'additionalProductsByPlants'
    | 'lossReason'
    | 'lossReasonNotes'
    | 'competitor'
    | 'competitorProducts'
  > & {
    products: ProjectOrQuoteProductEditableFieldsFormikType[];
    competitor: QuoteCompetitorWireFormat | null;
  }
>;

export const QuoteStatusSchemaWire: Yup.SchemaOf<QuoteStatus> = YupReference('');

/** This schema will be used to cast with the concatenation of all products from productsByPlants */
export const QuoteSchemaWire: QuoteSchemaWireType = Yup.object().shape({
  ...sharedQuoteSchemaFields,

  // Pass the nested wire competitor object through untouched. Casting it as a
  // formik reference (the previous behavior, via competitorLossFormikSchemaFields
  // in the shared fields) stripped its products/lossReasonNotes before the
  // request reached the server.
  competitor: Yup.mixed<QuoteCompetitorWireFormat>().nullable(),
  products: AdditionalProjectOrQuoteProductsFormikSchema,
}) as QuoteSchemaWireType;

export type QuoteSubmitType = 'save' | 'saveAsNewRevision' | 'saveAsNewQuote';

type FormikQuoteOpts = Partial<Quote> | undefined;

type ProjectProductMatchLine = {
  product: { id: string | null };
  usage: string | null;
  externalName: string | null;
  deliveryType: Enums.ProductDeliveryType | null;
};

const sharedFieldCount = (projectProduct: ProjectProduct, line: ProjectProductMatchLine): number =>
  [
    projectProduct.usage === line.usage,
    projectProduct.externalName === line.externalName,
    projectProduct.deliveryType === line.deliveryType,
  ].filter(Boolean).length;

export const matchProjectProductsToLines = (
  projectProducts: ProjectProduct[],
  lines: ProjectProductMatchLine[],
): (ProjectProduct | undefined)[] => {
  const bestCandidates = lines.map((line) => {
    const candidates = projectProducts.filter(
      (projectProduct) => projectProduct.product.id === line.product.id,
    );
    const bestScore = Math.max(
      ...candidates.map((projectProduct) => sharedFieldCount(projectProduct, line)),
    );
    return candidates.filter(
      (projectProduct) => sharedFieldCount(projectProduct, line) === bestScore,
    );
  });
  const lineIndexByProjectProduct = new Map<ProjectProduct, number>();
  const claim = (lineIndex: number, visited: Set<ProjectProduct>): boolean => {
    const claimed =
      bestCandidates[lineIndex].find(
        (projectProduct) => !lineIndexByProjectProduct.has(projectProduct),
      ) ??
      bestCandidates[lineIndex].find((projectProduct) => {
        if (visited.has(projectProduct)) return false;
        visited.add(projectProduct);
        const owner = lineIndexByProjectProduct.get(projectProduct);
        return hasValue(owner) && claim(owner, visited);
      });
    if (!hasValue(claimed)) return false;
    lineIndexByProjectProduct.set(claimed, lineIndex);
    return true;
  };
  lines.forEach((_, lineIndex) => claim(lineIndex, new Set()));
  const matches = new Map(
    [...lineIndexByProjectProduct].map(([projectProduct, lineIndex]) => [
      lineIndex,
      projectProduct,
    ]),
  );

  return lines.map((_, lineIndex) => matches.get(lineIndex) ?? bestCandidates[lineIndex][0]);
};

const getProductsByPlantsForKind = (
  quotePlants: QuotePlant[],
  quoteProducts: QuoteProduct[],
  kind: Enums.QuoteProductKind,
  isMoneySwitchOn: boolean,
  marginConfiguration?: Enums.MarginConfiguration,
  defaultMargins?: TenantDefaultMargins,
  projectProducts?: ProjectProduct[],
): ProductsByPlantFormikType[] => {
  // Determine which plants have products of this kind
  const plantIdsWithThisKind = new Set(
    quoteProducts.filter((qp) => qp.kind === kind).map((qp) => qp.product.plant.id),
  );
  const productsOfKind = quoteProducts.filter((qp) => qp.kind === kind);
  const projectProductMatches = matchProjectProductsToLines(projectProducts ?? [], productsOfKind);
  const projectPriceFor = (qp: QuoteProduct): Currency | undefined =>
    projectProductMatches[productsOfKind.indexOf(qp)]?.price;

  // Only include plants that have products of this kind
  return quotePlants
    .filter((quotePlant) => plantIdsWithThisKind.has(quotePlant.plant.id))
    .map((quotePlant): ProductsByPlantFormikType => {
      // Filter products for this plant and kind
      const productsForPlant = quoteProducts.filter(
        (qp) => qp.product.plant.id === quotePlant.plant.id && qp.kind === kind,
      );

      return {
        clientId: randomUUID(),
        plant: {
          id: quotePlant.plant.id,
          option: {
            value: quotePlant.plant.id,
            label: quotePlant.plant.name,
          },
        },
        plantDistance: quotePlant.plantDistance ?? null,
        plantDistanceUnit: quotePlant.plantDistanceUnit ?? null,
        plantDriveTime: quotePlant.plantDriveTime?.toISO() ?? null,
        products: productsForPlant.map((qp) =>
          QuoteProductToProjectOrQuoteProductFormik(
            qp,
            isMoneySwitchOn,
            marginConfiguration,
            defaultMargins,
            projectPriceFor(qp),
          ),
        ),
      };
    });
};

/**
 * Returns the form data for a Quote. Seeding from a project passes projectProducts
 * so lines revert to the project price rather than the product's list price.
 */
export const FormikQuote = (
  quoteData: FormikQuoteOpts,
  usesDispatchCustomer: boolean,
  submitType: QuoteSubmitType,
  isMoneySwitchOn: boolean,
  marginConfiguration: Enums.MarginConfiguration,
  defaultMargins: TenantDefaultMargins,
  projectProducts?: ProjectProduct[],
): QuoteFormikType => {
  // Group products by plants - for new quotes with no data, default to empty arrays
  const quotePlants = quoteData?.plants ?? [];
  // A new revision or duplicate re-reads each product's taxability; saving the same version keeps the lines'.
  const refreshesTaxability = submitType === 'saveAsNewRevision' || submitType === 'saveAsNewQuote';
  const quoteWithProducts =
    quoteData !== undefined
      ? NewQuote({
          products: (quoteData.products ?? []).map((qp) =>
            refreshesTaxability
              ? NewQuoteProduct({ ...qp, nonTaxable: qp.product.nonTaxable })
              : qp,
          ),
        })
      : Quote.zero();

  // If no plants provided but we have products, extract unique plants from products
  const plantsToUse =
    quotePlants.length > 0
      ? quotePlants
      : Array.from(
          new Set(
            quoteWithProducts.products
              .map((qp) => qp.product.plant.id)
              .filter((id): id is string => id != null),
          ),
        ).map((plantId) => {
          // Find a product with this plant to extract plant info
          const product = quoteWithProducts.products.find((qp) => qp.product.plant.id === plantId)!;
          return {
            plant: product.product.plant,
            plantDistance: null,
            plantDistanceUnit: null,
            plantDriveTime: null,
          };
        });

  const mainProductsByPlants = getProductsByPlantsForKind(
    plantsToUse,
    quoteWithProducts.products,
    Enums.QuoteProductKind.Primary,
    isMoneySwitchOn,
    marginConfiguration,
    defaultMargins,
    projectProducts,
  );
  const mainFormikQuoteProducts = quoteWithProducts
    .filterProducts(Enums.QuoteProductKind.Primary)
    .map((qp) =>
      QuoteProductToProjectOrQuoteProductFormik(
        qp,
        isMoneySwitchOn,
        marginConfiguration,
        defaultMargins,
      ),
    );

  const additionalFormikQuoteProducts = quoteWithProducts
    .filterProducts(Enums.QuoteProductKind.Additional)
    .map((qp) =>
      QuoteProductToProjectOrQuoteProductFormik(
        qp,
        isMoneySwitchOn,
        marginConfiguration,
        defaultMargins,
      ),
    );
  const additionalProductsByPlants = getProductsByPlantsForKind(
    plantsToUse,
    quoteWithProducts.products,
    Enums.QuoteProductKind.Additional,
    isMoneySwitchOn,
    marginConfiguration,
    defaultMargins,
  );

  const quotePriceEscalationsFormik =
    quoteData?.priceEscalations !== undefined
      ? quoteData.priceEscalations.map((qpe) => {
          const formikQpe = QuotePriceEscalationToFormik(qpe);
          return submitType === 'saveAsNewQuote'
            ? {
                ...formikQpe,
                appliedAt: null,
                appliedBy: null,
                appliedByName: null,
                isProcessed: false,
              }
            : formikQpe;
        })
      : [];

  const name =
    submitType === 'saveAsNewQuote' && isValidUUID(quoteData?.id)
      ? `${quoteData?.name} - Copy`
      : (quoteData?.name ?? null);

  const revisionNumber = ((): YupSchemaNumberType => {
    if (submitType === 'saveAsNewQuote') {
      return 1;
    }
    if (submitType === 'saveAsNewRevision') {
      return (quoteData?.revisionNumber ?? 0) + 1;
    }
    return quoteData?.revisionNumber ?? 1;
  })();

  // If we are creating a new quote, use the job number from the project.
  const getProjectJobNumber = (submitType: QuoteSubmitType, quoteData: FormikQuoteOpts) => {
    if (submitType === 'saveAsNewQuote') {
      return quoteData?.projectJobNumber ?? quoteData?.project?.projectJobNumber ?? null;
    }
    return quoteData?.projectJobNumber ?? null;
  };

  return {
    id: submitType === 'save' ? (quoteData?.id ?? NIL_UUID) : NIL_UUID,
    externalID: submitType === 'save' ? (quoteData?.externalID ?? null) : null,
    externalIDs: submitType === 'save' ? (quoteData?.externalIDs ?? []) : [],
    quoteNumber: submitType === 'saveAsNewQuote' ? '' : (quoteData?.quoteNumber ?? ''),
    name,
    integrationSummary: FormikNullableIntegrationSummary(quoteData?.integrationSummary),
    revisionNumber,
    version: submitType === 'save' ? (quoteData?.version ?? null) : null,
    status: FormikQuoteStatus(quoteData?.status),
    terms: quoteData?.terms ?? null,
    notes: quoteData?.notes ?? null,
    purchaseOrderNumber: quoteData?.purchaseOrderNumber ?? null,
    projectJobNumber: getProjectJobNumber(submitType, quoteData),
    expirationDate: quoteData?.expirationDate?.toISODate() ?? null,
    creationDate: quoteData?.creationDate?.toISODate() ?? DateTime.now().toISODate(),
    revisionDate: quoteData?.revisionDate?.toISODate() ?? null,
    firstSentAt: quoteData?.firstSentAt?.toISODate() ?? null,
    lastSentAt: quoteData?.lastSentAt?.toISODate() ?? null,
    user:
      quoteData?.user?.id === undefined || quoteData.user.id === NIL_UUID
        ? { id: null, option: null }
        : {
            id: quoteData.user.id,
            option: {
              value: quoteData.user.id,
              label: quoteData.user.fullName(),
            },
          },
    project:
      quoteData?.project?.id === undefined || quoteData.project.id === NIL_UUID
        ? { id: null, option: null }
        : {
            id: quoteData.project.id,
            option: {
              value: quoteData.project.id,
              label: quoteData.project.name,
            },
          },
    company:
      quoteData?.company?.id === undefined || quoteData.company.id === NIL_UUID
        ? { id: null, option: null }
        : {
            id: quoteData.company.id,
            option: {
              value: quoteData.company.id,
              label: quoteData.company.name,
              sublabels: NewCompany(quoteData.company).lookupSublabels(usesDispatchCustomer),
            },
          },
    contacts:
      quoteData?.contacts?.map((contact) => ({
        id: contact.id ?? null,
        option:
          contact.id !== NIL_UUID && contact.id !== ''
            ? {
                value: contact.id,
                label: contact.fullName(),
              }
            : null,
        isNewContact: false,
      })) ?? [],
    mainProductsByPlants,
    additionalProductsByPlants,
    quoteConfig:
      quoteData?.quoteConfig?.id === undefined || quoteData.quoteConfig.id === NIL_UUID
        ? { id: null, option: null }
        : {
            id: quoteData.quoteConfig.id,
            option: {
              value: quoteData.quoteConfig.id,
              label: quoteData.quoteConfig.name,
            },
          },
    priceEscalations: quotePriceEscalationsFormik,
    restrictOrdersToProjectProducts: quoteData?.restrictOrdersToProjectProducts ?? false,
    restrictOrdersToProjectMixProducts: quoteData?.restrictOrdersToProjectMixProducts ?? false,
    ...competitorLossToFormik(quoteData?.competitor),
  };
};

// We can safely assume `false` for `usesDispatchCustomer` as that only affects initial values.
export const EmptyFormikQuote = FormikQuote(
  undefined,
  false,
  'save',
  false,
  Enums.MarginConfiguration.Percentage,
  EmptyTenantDefaultMargins,
);

export const NewQuoteFromFormik = (values: QuoteFormikType): Quote => {
  // Flatten productsByPlants to products array
  const allMainProducts = values.mainProductsByPlants.flatMap((plantGroup) =>
    plantGroup.products.map((p) => ({
      ...p,
      nonTaxable: p.product.nonTaxable ?? false,
      deliveryCosts: NewDeliveryCostOrNullFromFormik(p.deliveryCosts),
      truckingType: hasValue(p.truckingType?.id) ? p.truckingType : null,
      additionalTruckingInfos: p.additionalTruckingInfos?.map((info) => ({
        ...info,
        haulRate: ParseNullableCurrency(info.haulRate),
        haulCost: ParseNullableCurrency(info.haulCost),
        truckingType: hasValue(info.truckingType?.id) ? info.truckingType : null,
      })),
    })),
  );

  const allAdditionalProducts = values.additionalProductsByPlants.flatMap((plantGroup) =>
    plantGroup.products.map((p) => ({
      ...p,
      nonTaxable: p.product.nonTaxable ?? false,
      deliveryCosts: NewDeliveryCostOrNullFromFormik(p.deliveryCosts),
      truckingType: hasValue(p.truckingType?.id) ? p.truckingType : null,
      additionalTruckingInfos: p.additionalTruckingInfos?.map((info) => ({
        ...info,
        haulRate: ParseNullableCurrency(info.haulRate),
        haulCost: ParseNullableCurrency(info.haulCost),
        truckingType: hasValue(info.truckingType?.id) ? info.truckingType : null,
      })),
    })),
  );

  const products = [...allMainProducts, ...allAdditionalProducts];

  const priceEscalations = values.priceEscalations.map((qpe) => ({
    ...qpe,
    changeRatio: escalationPercentToChangeRatio(qpe.changeRatio),
  }));

  // Transform contacts data
  const contacts = values.contacts.map((contact) => {
    if (
      contact.isNewContact &&
      contact.contact?.firstName !== undefined &&
      contact.contact?.firstName !== '' &&
      contact.contact?.lastName !== undefined &&
      contact.contact?.lastName !== ''
    ) {
      const newContact = contact.contact;
      return {
        id: null,
        firstName: newContact.firstName,
        lastName: newContact.lastName,
        emailAddress: newContact.emailAddress ?? '',
        phoneNumber: newContact.phoneNumber ?? '',
        jobTitle: newContact.jobTitle ?? '',
        notes: newContact.notes,
        company: {
          id: values.company?.id ?? '',
          name: values.company?.option?.label ?? '',
        },
        isArchived: false,
        fullName: (): string => `${newContact.firstName} ${newContact.lastName}`.trim(),
      };
    }
    // For existing contacts, return the reference
    return {
      id: contact.id,
    };
  });

  // Gather unique plants from mainProductsByPlants and additionalProductsByPlants
  const allProductsByPlants = [
    ...values.mainProductsByPlants,
    ...values.additionalProductsByPlants,
  ];

  const plantMap = new Map();
  allProductsByPlants.forEach((plantGroup) => {
    if (plantGroup.plant?.id) {
      plantMap.set(plantGroup.plant.id, {
        plant: {
          id: plantGroup.plant.id,
          name: plantGroup.plant.option?.label ?? '',
        },
        plantDistance: plantGroup.plantDistance,
        plantDistanceUnit: plantGroup.plantDistanceUnit,
        plantDriveTime: plantGroup.plantDriveTime
          ? (Duration.fromISO(plantGroup.plantDriveTime) ?? null)
          : null,
      });
    }
  });

  const plants = Array.from(plantMap.values());

  const wireQuote = new Quote({
    ...values,
    contacts,
    products,
    priceEscalations,
    plants,
    competitor: competitorLossFromFormik(values),
  });

  return new Quote(QuoteSchemaWire.cast(wireQuote));
};

/* Converts form data to a QuoteProduct domain object. */
export const NewQuoteProductFromFormik = (
  values: Omit<ProjectOrQuoteProductFormikType, 'displayOnly'>,
  dirtyEdits?: PartialDeep<Omit<ProjectOrQuoteProductFormikType, 'displayOnly'>>,
): QuoteProduct => {
  const input: Omit<ProjectOrQuoteProductFormikType, 'displayOnly'> = mergeObjects(
    {},
    values,
    dirtyEdits,
  );

  let castData: Parameters<typeof NewQuoteProductFromWireFormat>[0];
  try {
    castData = RemoveNullProperties(
      QuoteSchemaFormik.cast({ additionalProductsByPlants: [{ products: [input] }] })
        .additionalProductsByPlants?.[0]?.products?.[0] ?? {},
    );
  } catch (e) {
    try {
      QuoteSchemaFormik.validateSyncAt(
        'additionalProductsByPlants.0.products',
        { additionalProductsByPlants: [{ products: [input] }] },
        { strict: true, abortEarly: false },
      );
      logger.error(`NewQuoteProductFromFormik parsing failed, but validation succeeded?! ${e}`);
    } catch (error) {
      const details =
        error instanceof Yup.ValidationError
          ? error.inner.map((inner) => `${inner.path}: ${inner.type}`).join('\n')
          : '';
      logger.error(`NewQuoteProductFromFormik parsing failed. ${error}\n${details}`);
    }
    castData = {};
  }

  return NewQuoteProductFromWireFormat(castData);
};

export type QuotePolicyViolationFormikType = Omit<
  QuotePolicyViolationWireFormat,
  'id' | 'quotePolicy'
> & {
  state: Enums.QuotePolicyViolationState;
  comment: string | null;
};

export const QuotePolicyViolationSchema: Yup.SchemaOf<QuotePolicyViolationFormikType> = Yup.object()
  .shape({
    state: YupEnum(Enums.QuotePolicyViolationState, 'State'),
    comment: YupNullableString('Comment').test(
      'required-if-rejected',
      'Comment required',
      (value, ctx) =>
        ctx.parent.state === Enums.QuotePolicyViolationState.Approved ||
        (value !== null && value !== ''),
    ),
    reviewerId: YupNullableReference('Reviewer'),
    reviewerFirstName: YupNullableString('Reviewer first name'),
    reviewerLastName: YupNullableString('Reviewer last name'),
    reviewedAt: YupNullableLocalDate('Reviewed at'),
  })
  .required();

export const FormikQuotePolicyViolation = (defaults: {
  state: Enums.QuotePolicyViolationState;
}): QuotePolicyViolationFormikType => ({
  state: defaults.state,
  comment: '',
  reviewerId: null,
  reviewerFirstName: null,
  reviewerLastName: null,
  reviewedAt: null,
});

export const ProjectProductToQuoteProductFormik = (
  projectProduct: ProjectProduct,
  isMoneySwitchOn: boolean,
  marginConfiguration?: Enums.MarginConfiguration,
  defaultMargins?: TenantDefaultMargins,
  tierPrice?: Currency | null,
): ProjectOrQuoteProductFormikType =>
  QuoteProductToProjectOrQuoteProductFormik(
    NewQuoteProduct({
      class: null,
      deliveryCosts: projectProduct.deliveryCosts,
      externalName: hasStringValue(projectProduct.externalName)
        ? projectProduct.externalName
        : projectProduct.product.externalName(),
      kind: Enums.QuoteProductKind.Primary,
      price: tierPrice ?? projectProduct.price,
      product: projectProduct.product,
      nonTaxable: projectProduct.product.nonTaxable,
      quantity: projectProduct.quantity,
      usage: projectProduct.usage,
      deliveryType: projectProduct.deliveryType,
      haulRate: projectProduct.haulRate,
      minimumHaulCharge: projectProduct.minimumHaulCharge,
      taxRate: projectProduct.taxRate,
      truckingType: projectProduct.truckingType,
      additionalTruckingInfos: projectProduct.additionalTruckingInfos,
      haulCost: projectProduct.haulCost,
    }),
    isMoneySwitchOn,
    marginConfiguration ?? Enums.MarginConfiguration.Percentage,
    defaultMargins ?? EmptyTenantDefaultMargins,
    projectProduct.price,
  );

// This is used for defaulting when we add a new product, or when we add a plant that has no delivery costs.
const zeroDeliveryCost = {
  deliveryCostOverride: Cost.zero(),
};

export const QuoteConfigProductToQuoteProductFormik = (
  qcp: QuoteConfigProduct,
  isMoneySwitchOn: boolean,
  defaultMargins: TenantDefaultMargins,
  marginConfiguration?: Enums.MarginConfiguration,
): ProjectOrQuoteProductFormikType => {
  const resolvedMarginConfiguration = marginConfiguration ?? Enums.MarginConfiguration.Percentage;
  const deliveryCosts = qcp.product.plant.deliveryCosts ?? zeroDeliveryCost;
  const quoteProduct = NewQuoteProduct({
    class: null,
    externalName: qcp.externalName ?? qcp.product.externalName(),
    kind: Enums.QuoteProductKind.Additional,
    product: qcp.product,
    nonTaxable: qcp.product.nonTaxable,
    quantity: null,
    deliveryCosts,
    usage: qcp.usage,
  });
  const price =
    qcp.product.listPrice ??
    quoteProduct.calculateSuggestedPrice(resolvedMarginConfiguration, defaultMargins) ??
    Currency.zero();
  return QuoteProductToProjectOrQuoteProductFormik(
    NewQuoteProduct({ ...quoteProduct, price }),
    isMoneySwitchOn,
    resolvedMarginConfiguration,
    defaultMargins,
  );
};
